import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  CreditCard,
  Receipt,
  Building2,
  TrendingUp,
  Calendar,
  CheckCircle2,
  AlertTriangle,
  ExternalLink,
  RefreshCw,
  Link2,
  Plus,
  Trash2,
  Loader2,
  ArrowUpRight,
  Settings,
  XCircle,
  Banknote,
  LayoutList,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { StatCard } from '../../../components/ui/stat-card';
import PageInfoButton from '@/components/PageInfoButton';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
  'X-CSRF-Token': getCsrfToken(),
});

const formatCurrency = (amount: number, currency = 'GBP') =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(amount / 100);

const formatDate = (dateStr: string) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const getStatusBadge = (status: string) => {
  switch (status) {
    case 'active':
      return <Badge variant="success">Active</Badge>;
    case 'paid':
      return <Badge variant="success">Paid</Badge>;
    case 'past_due':
      return <Badge variant="warning">Past Due</Badge>;
    case 'unpaid':
      return <Badge variant="danger">Unpaid</Badge>;
    case 'cancelled':
      return <Badge variant="default">Cancelled</Badge>;
    case 'pending':
      return <Badge variant="warning">Pending</Badge>;
    case 'connected':
      return <Badge variant="success">Connected</Badge>;
    default:
      return <Badge variant="default">{status}</Badge>;
  }
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PartnerSubscription {
  id: number;
  plan_name: string;
  status: string;
  company_count: number;
  max_companies: number;
  current_period_start: string;
  current_period_end: string;
  amount: number;
  currency: string;
}

interface PartnerPlan {
  id: string;
  name: string;
  description: string;
  price: number;
  currency: string;
  billing_cycle: string;
  features?: string[];
}

interface PartnerInvoice {
  id: number;
  invoice_number: string;
  amount: number;
  currency: string;
  status: string;
  period_start: string;
  period_end: string;
  created_at: string;
  pdf_url?: string;
}

interface ConnectStatus {
  status: 'not_connected' | 'pending' | 'active';
  account_id?: string;
  business_name?: string;
  application_fee_percent?: number;
  charges_enabled?: boolean;
  payouts_enabled?: boolean;
}

interface CompanyPlan {
  id: number;
  name: string;
  description: string;
  price: number;
  currency: string;
  billing_cycle: string;
  stripe_price_id?: string;
}

interface CompanyBillingRow {
  company_id: number;
  company_name: string;
  subscription_status: string;
  plan_name?: string;
  amount?: number;
  currency?: string;
  current_period_end?: string;
}

type TabKey = 'subscription' | 'connect' | 'company-plans' | 'company-billing' | 'revenue';

// ---------------------------------------------------------------------------
// Skeleton loader shared across tabs
// ---------------------------------------------------------------------------

function TabSkeleton() {
  return (
    <div className="animate-pulse space-y-4">
      <div className="h-6 bg-gray-200 rounded w-1/3" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[1, 2].map((i) => (
          <div key={i} className="h-32 bg-gray-200 rounded-xl" />
        ))}
      </div>
      <div className="h-48 bg-gray-200 rounded-xl" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 1: My Subscription
// ---------------------------------------------------------------------------

function MySubscriptionTab() {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<PartnerSubscription | null>(null);
  const [plans, setPlans] = useState<PartnerPlan[]>([]);
  const [invoices, setInvoices] = useState<PartnerInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [subRes, plansRes, invRes] = await Promise.all([
        fetch('/api/billing/partner/subscription', { headers: authHeaders() }).catch(() => null),
        fetch('/api/billing/partner/plans', { headers: authHeaders() }).catch(() => null),
        fetch('/api/billing/partner/invoices', { headers: authHeaders() }).catch(() => null),
      ]);
      if (subRes?.ok) {
        const d = await subRes.json();
        if (d.success) setSubscription(d.subscription);
      }
      if (plansRes?.ok) {
        const d = await plansRes.json();
        if (d.success) setPlans(d.plans || []);
      }
      if (invRes?.ok) {
        const d = await invRes.json();
        if (d.success) setInvoices(d.invoices || []);
      }
    } catch {
      // endpoints may not exist yet
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCheckout = async (planId: string) => {
    setActionLoading('checkout');
    try {
      const res = await fetch('/api/billing/partner/checkout', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ plan_id: planId }),
      });
      const result = await res.json();
      if (result.url) window.location.href = result.url;
    } catch {
      // handle error silently
    }
    setActionLoading(null);
  };

  const handlePortal = async () => {
    setActionLoading('portal');
    try {
      const res = await fetch('/api/billing/partner/portal', {
        method: 'POST',
        headers: authHeaders(),
      });
      const result = await res.json();
      if (result.url) window.location.href = result.url;
    } catch {
      // handle error silently
    }
    setActionLoading(null);
  };

  const handleCancel = async () => {
    if (!confirm('Are you sure you want to cancel your subscription?')) return;
    setActionLoading('cancel');
    try {
      const res = await fetch('/api/billing/partner/cancel', {
        method: 'POST',
        headers: authHeaders(),
      });
      const result = await res.json();
      if (result.success) await load();
    } catch {
      // handle error silently
    }
    setActionLoading(null);
  };

  if (loading) return <TabSkeleton />;

  return (
    <div className="space-y-6">
      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Subscription"
          value={subscription?.plan_name || 'No Plan'}
          icon={CreditCard}
          variant={subscription?.status === 'active' ? 'success' : 'warning'}
          description={subscription ? `Status: ${subscription.status}` : 'No active subscription'}
        />
        <StatCard
          title="Companies"
          value={subscription ? `${subscription.company_count} / ${subscription.max_companies}` : '0'}
          icon={Building2}
          variant="info"
          description="Managed companies"
        />
        <StatCard
          title="Monthly Cost"
          value={subscription ? formatCurrency(subscription.amount, subscription.currency) : '—'}
          icon={TrendingUp}
          variant="default"
          description={subscription ? `Billed ${subscription.currency?.toUpperCase()}` : 'No billing'}
        />
        <StatCard
          title="Next Billing"
          value={subscription?.current_period_end ? formatDate(subscription.current_period_end) : '—'}
          icon={Calendar}
          variant="default"
          description={subscription ? 'Current period' : 'No billing cycle'}
        />
      </div>

      {/* Current Subscription */}
      {subscription ? (
        <Card className="border border-gray-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-blue-600" />
                Subscription Details
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePortal}
                  disabled={actionLoading === 'portal'}
                >
                  {actionLoading === 'portal' ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Settings className="w-4 h-4 mr-2" />
                  )}
                  Manage Subscription
                </Button>
                {subscription.status === 'active' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancel}
                    disabled={actionLoading === 'cancel'}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                  >
                    {actionLoading === 'cancel' ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <XCircle className="w-4 h-4 mr-2" />
                    )}
                    Cancel
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-gray-500 font-medium uppercase">Plan</p>
                <p className="text-sm font-semibold text-gray-900 mt-1">{subscription.plan_name}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium uppercase">Status</p>
                <div className="mt-1">{getStatusBadge(subscription.status)}</div>
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium uppercase">Period</p>
                <p className="text-sm text-gray-700 mt-1">
                  {formatDate(subscription.current_period_start)} — {formatDate(subscription.current_period_end)}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium uppercase">Amount</p>
                <p className="text-sm font-semibold text-gray-900 mt-1">
                  {formatCurrency(subscription.amount, subscription.currency)} / month
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border border-amber-200 bg-amber-50">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-900">No Active Subscription</p>
                <p className="text-sm text-amber-700 mt-1">
                  Choose a plan below to get started with your partner subscription.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Available Plans */}
      {plans.length > 0 && !subscription && (
        <Card className="border border-gray-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <LayoutList className="w-5 h-5 text-indigo-600" />
              Available Plans
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {plans.map((plan) => (
                <div
                  key={plan.id}
                  className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors"
                >
                  <h3 className="text-base font-semibold text-gray-900">{plan.name}</h3>
                  {plan.description && (
                    <p className="text-sm text-gray-500 mt-1">{plan.description}</p>
                  )}
                  <p className="text-xl font-bold text-gray-900 mt-3">
                    {formatCurrency(plan.price, plan.currency)}
                    <span className="text-sm font-normal text-gray-500"> / {plan.billing_cycle}</span>
                  </p>
                  {plan.features && plan.features.length > 0 && (
                    <ul className="mt-3 space-y-1">
                      {plan.features.map((f, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                          <CheckCircle2 className="w-3.5 h-3.5 text-green-500 shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                  )}
                  <Button
                    className="w-full mt-4"
                    onClick={() => handleCheckout(plan.id)}
                    disabled={actionLoading === 'checkout'}
                  >
                    {actionLoading === 'checkout' ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <ArrowUpRight className="w-4 h-4 mr-2" />
                    )}
                    Subscribe
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invoices */}
      <Card className="border border-gray-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-purple-600" />
            Invoices
          </CardTitle>
        </CardHeader>
        <CardContent>
          {invoices.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Receipt className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">No invoices yet</p>
              <p className="text-xs text-gray-400 mt-1">Invoices will appear here once billing is active</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Invoice</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Period</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Amount</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {invoices.map((inv) => (
                    <tr key={inv.id} className="hover:bg-gray-50">
                      <td className="py-2.5 px-3 text-left font-medium text-gray-900">{inv.invoice_number}</td>
                      <td className="py-2.5 px-3 text-left text-gray-600">{formatDate(inv.created_at)}</td>
                      <td className="py-2.5 px-3 text-left text-gray-600">
                        {formatDate(inv.period_start)} — {formatDate(inv.period_end)}
                      </td>
                      <td className="py-2.5 px-3 text-left font-medium text-gray-900">
                        {formatCurrency(inv.amount, inv.currency)}
                      </td>
                      <td className="py-2.5 px-3 text-left">{getStatusBadge(inv.status)}</td>
                      <td className="py-2.5 px-3 text-left">
                        {inv.pdf_url && (
                          <a
                            href={inv.pdf_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
                          >
                            Download <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 2: Stripe Connect
// ---------------------------------------------------------------------------

function StripeConnectTab() {
  const [connectStatus, setConnectStatus] = useState<ConnectStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/billing/partner/connect/status', { headers: authHeaders() });
      if (res.ok) {
        const d = await res.json();
        if (d.success) setConnectStatus(d.connect ?? d);
      }
    } catch {
      // endpoint may not exist yet
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleOnboard = async () => {
    setActionLoading(true);
    try {
      const res = await fetch('/api/billing/partner/connect/onboard', {
        method: 'POST',
        headers: authHeaders(),
      });
      const result = await res.json();
      if (result.onboardingUrl) window.location.href = result.onboardingUrl;
    } catch {
      // handle error
    }
    setActionLoading(false);
  };

  const handleRefresh = async () => {
    setActionLoading(true);
    try {
      const res = await fetch('/api/billing/partner/connect/refresh', {
        method: 'POST',
        headers: authHeaders(),
      });
      const result = await res.json();
      if (result.url) window.location.href = result.url;
    } catch {
      // handle error
    }
    setActionLoading(false);
  };

  if (loading) return <TabSkeleton />;

  const status = connectStatus?.status || 'not_connected';

  return (
    <div className="space-y-6">
      <Card className="border border-gray-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-indigo-600" />
            Stripe Connect Account
          </CardTitle>
        </CardHeader>
        <CardContent>
          {status === 'not_connected' && (
            <div className="text-center py-10">
              <Link2 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-semibold text-gray-900">Connect your Stripe Account</h3>
              <p className="text-sm text-gray-500 mt-2 max-w-md mx-auto">
                Link your Stripe account to receive payments from your companies and manage their subscriptions directly.
              </p>
              <Button className="mt-6" onClick={handleOnboard} disabled={actionLoading}>
                {actionLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ArrowUpRight className="w-4 h-4 mr-2" />
                )}
                Connect your Stripe Account
              </Button>
            </div>
          )}

          {status === 'pending' && (
            <div className="text-center py-10">
              <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-400" />
              <h3 className="text-lg font-semibold text-gray-900">Setup Incomplete</h3>
              <p className="text-sm text-gray-500 mt-2 max-w-md mx-auto">
                Your Stripe Connect onboarding is not yet complete. Please finish the setup to start accepting payments.
              </p>
              <Button className="mt-6" onClick={handleRefresh} disabled={actionLoading}>
                {actionLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Complete Setup
              </Button>
            </div>
          )}

          {status === 'active' && connectStatus && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle2 className="w-6 h-6 text-green-600 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-green-900">Connected</p>
                  <p className="text-sm text-green-700">
                    Your Stripe Connect account is active and ready to process payments.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-gray-500 font-medium uppercase">Account ID</p>
                  <p className="text-sm font-mono text-gray-900 mt-1">{connectStatus.account_id || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium uppercase">Business Name</p>
                  <p className="text-sm font-semibold text-gray-900 mt-1">{connectStatus.business_name || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium uppercase">Charges Enabled</p>
                  <div className="mt-1">
                    {connectStatus.charges_enabled ? (
                      <Badge variant="success">Yes</Badge>
                    ) : (
                      <Badge variant="danger">No</Badge>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium uppercase">Payouts Enabled</p>
                  <div className="mt-1">
                    {connectStatus.payouts_enabled ? (
                      <Badge variant="success">Yes</Badge>
                    ) : (
                      <Badge variant="danger">No</Badge>
                    )}
                  </div>
                </div>
              </div>

              {connectStatus.application_fee_percent != null && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <span className="font-semibold">Application Fee:</span>{' '}
                    {connectStatus.application_fee_percent}% is collected on each company transaction.
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 3: Company Plans
// ---------------------------------------------------------------------------

function CompanyPlansTab() {
  const [plans, setPlans] = useState<CompanyPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    price: '',
    currency: 'GBP',
    billing_cycle: 'month',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/billing/partner/connect/plans', { headers: authHeaders() });
      if (res.ok) {
        const d = await res.json();
        if (d.success) setPlans(d.plans || []);
      }
    } catch {
      // endpoint may not exist
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const res = await fetch('/api/billing/partner/connect/plans', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          ...form,
          price: Math.round(parseFloat(form.price) * 100), // convert to pence/cents
        }),
      });
      const result = await res.json();
      if (result.success) {
        setForm({ name: '', description: '', price: '', currency: 'GBP', billing_cycle: 'month' });
        setShowForm(false);
        await load();
      }
    } catch {
      // handle error
    }
    setFormLoading(false);
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await fetch('/api/billing/partner/connect/sync-plans', {
        method: 'POST',
        headers: authHeaders(),
      });
      const result = await res.json();
      if (result.success) await load();
    } catch {
      // handle error
    }
    setSyncing(false);
  };

  if (loading) return <TabSkeleton />;

  return (
    <div className="space-y-6">
      {/* Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Company Subscription Plans</h2>
          <p className="text-sm text-gray-500 mt-0.5">Create and manage plans your companies can subscribe to.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleSync} disabled={syncing}>
            {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Sync to Stripe
          </Button>
          <Button onClick={() => setShowForm(!showForm)}>
            <Plus className="w-4 h-4 mr-2" />
            Create Plan
          </Button>
        </div>
      </div>

      {/* Create Form */}
      {showForm && (
        <Card className="border border-blue-200 bg-blue-50/30">
          <CardHeader>
            <CardTitle className="text-base">New Company Plan</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreate} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Plan Name</label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                  placeholder="e.g. Standard"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                  placeholder="Brief description"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Price (major units)</label>
                <input
                  type="number"
                  required
                  min="0"
                  step="0.01"
                  value={form.price}
                  onChange={(e) => setForm((p) => ({ ...p, price: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                  placeholder="49.99"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Currency</label>
                <select
                  value={form.currency}
                  onChange={(e) => setForm((p) => ({ ...p, currency: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none bg-white"
                >
                  <option value="GBP">GBP</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Billing Cycle</label>
                <select
                  value={form.billing_cycle}
                  onChange={(e) => setForm((p) => ({ ...p, billing_cycle: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none bg-white"
                >
                  <option value="month">Monthly</option>
                  <option value="year">Yearly</option>
                </select>
              </div>
              <div className="flex items-end gap-2">
                <Button type="submit" disabled={formLoading}>
                  {formLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                  Create
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Plans Table */}
      <Card className="border border-gray-200">
        <CardContent className="p-0">
          {plans.length === 0 ? (
            <div className="text-center py-10 text-gray-500">
              <LayoutList className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">No company plans created yet</p>
              <p className="text-xs text-gray-400 mt-1">Create a plan to start billing your companies</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Name</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Description</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Price</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Cycle</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Stripe</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {plans.map((plan) => (
                    <tr key={plan.id} className="hover:bg-gray-50">
                      <td className="py-3 px-4 text-left font-medium text-gray-900">{plan.name}</td>
                      <td className="py-3 px-4 text-left text-gray-600">{plan.description || '—'}</td>
                      <td className="py-3 px-4 text-left font-medium text-gray-900">
                        {formatCurrency(plan.price, plan.currency)}
                      </td>
                      <td className="py-3 px-4 text-left text-gray-600 capitalize">{plan.billing_cycle}</td>
                      <td className="py-3 px-4 text-left">
                        {plan.stripe_price_id ? (
                          <Badge variant="success">Synced</Badge>
                        ) : (
                          <Badge variant="warning">Not synced</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab 4: Company Billing
// ---------------------------------------------------------------------------

function CompanyBillingTab() {
  const [companies, setCompanies] = useState<CompanyBillingRow[]>([]);
  const [plans, setPlans] = useState<CompanyPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<Record<number, number>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [compRes, plansRes] = await Promise.all([
        fetch('/api/billing/partner/connect/checkout', { headers: authHeaders() }).catch(() => null),
        fetch('/api/billing/partner/connect/plans', { headers: authHeaders() }).catch(() => null),
      ]);
      // The GET on checkout endpoint returns company billing data
      if (compRes?.ok) {
        const d = await compRes.json();
        if (d.success) setCompanies(d.companies || []);
      }
      if (plansRes?.ok) {
        const d = await plansRes.json();
        if (d.success) setPlans(d.plans || []);
      }
    } catch {
      // endpoint may not exist
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSubscribe = async (companyId: number) => {
    const planId = selectedPlan[companyId];
    if (!planId) return;
    setActionLoading(companyId);
    try {
      const res = await fetch('/api/billing/partner/connect/checkout', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ company_id: companyId, plan_id: planId }),
      });
      const result = await res.json();
      if (result.url) window.location.href = result.url;
      if (result.success) await load();
    } catch {
      // handle error
    }
    setActionLoading(null);
  };

  const totalRevenue = companies.reduce((sum, c) => sum + (c.amount || 0), 0);
  const activeCount = companies.filter((c) => c.subscription_status === 'active').length;

  if (loading) return <TabSkeleton />;

  return (
    <div className="space-y-6">
      {/* Revenue Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          title="Total Companies"
          value={String(companies.length)}
          icon={Building2}
          variant="info"
          description="Companies under your management"
        />
        <StatCard
          title="Active Subscriptions"
          value={String(activeCount)}
          icon={CheckCircle2}
          variant="success"
          description={`${companies.length > 0 ? Math.round((activeCount / companies.length) * 100) : 0}% of companies`}
        />
        <StatCard
          title="Monthly Revenue"
          value={formatCurrency(totalRevenue)}
          icon={Banknote}
          variant="default"
          description="From active company subscriptions"
        />
      </div>

      {/* Company Table */}
      <Card className="border border-gray-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-blue-600" />
            Company Subscriptions
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {companies.length === 0 ? (
            <div className="text-center py-10 text-gray-500">
              <Building2 className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">No companies found</p>
              <p className="text-xs text-gray-400 mt-1">Companies will appear here once added to your account</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Company</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Plan</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Status</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Amount</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Renews</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {companies.map((co) => (
                    <tr key={co.company_id} className="hover:bg-gray-50">
                      <td className="py-3 px-4 text-left font-medium text-gray-900">{co.company_name}</td>
                      <td className="py-3 px-4 text-left text-gray-600">{co.plan_name || '—'}</td>
                      <td className="py-3 px-4 text-left">{getStatusBadge(co.subscription_status || 'none')}</td>
                      <td className="py-3 px-4 text-left font-medium text-gray-900">
                        {co.amount ? formatCurrency(co.amount, co.currency) : '—'}
                      </td>
                      <td className="py-3 px-4 text-left text-gray-600">
                        {co.current_period_end ? formatDate(co.current_period_end) : '—'}
                      </td>
                      <td className="py-3 px-4 text-left">
                        {co.subscription_status !== 'active' && plans.length > 0 && (
                          <div className="flex items-center justify-end gap-2">
                            <select
                              value={selectedPlan[co.company_id] || ''}
                              onChange={(e) =>
                                setSelectedPlan((p) => ({ ...p, [co.company_id]: Number(e.target.value) }))
                              }
                              className="rounded-md border border-gray-300 px-2 py-1 text-xs bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                            >
                              <option value="">Select plan</option>
                              {plans.map((pl) => (
                                <option key={pl.id} value={pl.id}>
                                  {pl.name} — {formatCurrency(pl.price, pl.currency)}
                                </option>
                              ))}
                            </select>
                            <Button
                              size="sm"
                              onClick={() => handleSubscribe(co.company_id)}
                              disabled={!selectedPlan[co.company_id] || actionLoading === co.company_id}
                            >
                              {actionLoading === co.company_id ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                'Subscribe'
                              )}
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Revenue Dashboard Tab
// ---------------------------------------------------------------------------

function RevenueTab() {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<any>(null);
  const [transactions, setTransactions] = useState<any[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const [summaryRes, txRes] = await Promise.all([
          fetch('/api/billing/partner/connect/revenue', { headers: authHeaders() }),
          fetch('/api/billing/partner/connect/transactions?limit=20', { headers: authHeaders() }),
        ]);
        if (summaryRes.ok) {
          const s = await summaryRes.json();
          setSummary(s.data);
        }
        if (txRes.ok) {
          const t = await txRes.json();
          setTransactions(t.data || []);
        }
      } catch (err) {
        console.error('Revenue fetch error', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <TabSkeleton />;

  const fmtCur = (n: number, cur = 'GBP') =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency: cur }).format(n);

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Total Revenue" value={fmtCur(summary?.total_gross ?? 0)} icon={<Banknote className="w-5 h-5" />} />
        <StatCard title="Platform Fees" value={fmtCur(summary?.total_fees ?? 0)} icon={<Receipt className="w-5 h-5" />} />
        <StatCard title="Your Earnings" value={fmtCur(summary?.total_net ?? 0)} icon={<TrendingUp className="w-5 h-5" />} />
        <StatCard title="Transactions" value={String(summary?.transaction_count ?? 0)} icon={<CreditCard className="w-5 h-5" />} />
      </div>

      {/* Monthly Breakdown */}
      {summary?.by_month?.length > 0 && (
        <Card>
          <CardHeader><CardTitle>Monthly Revenue</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-slate-500">
                    <th className="pb-2 pr-4">Month</th>
                    <th className="pb-2 pr-4 text-right">Gross</th>
                    <th className="pb-2 pr-4 text-right">Fees</th>
                    <th className="pb-2 pr-4 text-right">Net Earnings</th>
                    <th className="pb-2 text-right">Transactions</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.by_month.map((m: any) => (
                    <tr key={m.month} className="border-b last:border-0">
                      <td className="py-2 pr-4 text-left font-medium">{m.month}</td>
                      <td className="py-2 pr-4 text-left">{fmtCur(m.gross)}</td>
                      <td className="py-2 pr-4 text-left text-red-600">-{fmtCur(m.fees)}</td>
                      <td className="py-2 pr-4 text-left font-semibold text-emerald-700">{fmtCur(m.net)}</td>
                      <td className="py-2 text-left">{m.transactions}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Revenue by Company */}
      {summary?.by_company?.length > 0 && (
        <Card>
          <CardHeader><CardTitle>Revenue by Company</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-slate-500">
                    <th className="pb-2 pr-4">Company</th>
                    <th className="pb-2 pr-4 text-right">Gross</th>
                    <th className="pb-2 pr-4 text-right">Fees</th>
                    <th className="pb-2 pr-4 text-right">Net Earnings</th>
                    <th className="pb-2 text-right">Payments</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.by_company.map((c: any) => (
                    <tr key={c.company_id} className="border-b last:border-0">
                      <td className="py-2 pr-4 text-left font-medium">{c.company_name}</td>
                      <td className="py-2 pr-4 text-left">{fmtCur(c.gross)}</td>
                      <td className="py-2 pr-4 text-left text-red-600">-{fmtCur(c.fees)}</td>
                      <td className="py-2 pr-4 text-left font-semibold text-emerald-700">{fmtCur(c.net)}</td>
                      <td className="py-2 text-left">{c.transactions}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Transactions */}
      <Card>
        <CardHeader><CardTitle>Recent Transactions</CardTitle></CardHeader>
        <CardContent>
          {transactions.length === 0 ? (
            <p className="text-slate-500 text-sm py-4 text-center">No transactions recorded yet. Revenue will appear here once companies start paying through your Stripe Connect account.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-slate-500">
                    <th className="pb-2 pr-4">Date</th>
                    <th className="pb-2 pr-4">Company</th>
                    <th className="pb-2 pr-4 text-right">Amount</th>
                    <th className="pb-2 pr-4 text-right">Fee</th>
                    <th className="pb-2 text-right">Your Earnings</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((tx: any) => (
                    <tr key={tx.id} className="border-b last:border-0">
                      <td className="py-2 pr-4 text-left">{formatDate(tx.created_at)}</td>
                      <td className="py-2 pr-4 text-left font-medium">{tx.company_name}</td>
                      <td className="py-2 pr-4 text-left">{fmtCur(tx.gross_amount, tx.currency)}</td>
                      <td className="py-2 pr-4 text-left text-red-600">-{fmtCur(tx.application_fee, tx.currency)}</td>
                      <td className="py-2 text-left font-semibold text-emerald-700">{fmtCur(tx.net_to_partner, tx.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function PartnerBilling() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<TabKey>('subscription');
  const [connectStatus, setConnectStatus] = useState<ConnectStatus | null>(null);

  // Fetch Connect status at the top level so we know which tabs to show
  useEffect(() => {
    const checkConnect = async () => {
      try {
        const res = await fetch('/api/billing/partner/connect/status', { headers: authHeaders() });
        if (res.ok) {
          const d = await res.json();
          if (d.success) setConnectStatus(d.connect ?? d);
        }
      } catch {
        // endpoint may not exist
      }
    };
    checkConnect();
  }, []);

  const connectActive = connectStatus?.status === 'active';

  const tabs: { key: TabKey; label: string; icon: React.ElementType; visible: boolean }[] = [
    { key: 'subscription', label: 'My Subscription', icon: CreditCard, visible: true },
    { key: 'connect', label: 'Stripe Connect', icon: Link2, visible: true },
    { key: 'company-plans', label: 'Company Plans', icon: LayoutList, visible: connectActive },
    { key: 'company-billing', label: 'Company Billing', icon: Banknote, visible: connectActive },
    { key: 'revenue', label: 'Revenue Dashboard', icon: TrendingUp, visible: connectActive },
  ];

  // If the active tab becomes hidden, reset to subscription
  useEffect(() => {
    const current = tabs.find((t) => t.key === activeTab);
    if (current && !current.visible) {
      setActiveTab('subscription');
    }
  }, [connectActive, activeTab]);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Billing & Payments</h1><PageInfoButton /></div>
          <p className="text-slate-500 text-sm mt-0.5">
            Manage your subscription, Stripe Connect, and company billing
          </p>
        </div>
        <Button variant="outline" onClick={() => window.location.reload()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex flex-wrap gap-1 -mb-px" aria-label="Billing tabs">
          {tabs
            .filter((t) => t.visible)
            .map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`
                    inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium
                    border-b-2 transition-colors whitespace-nowrap
                    ${
                      isActive
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                  {/* Show abbreviated label on small screens */}
                  <span className="sm:hidden">{tab.label.split(' ').pop()}</span>
                </button>
              );
            })}
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'subscription' && <MySubscriptionTab />}
        {activeTab === 'connect' && <StripeConnectTab />}
        {activeTab === 'company-plans' && connectActive && <CompanyPlansTab />}
        {activeTab === 'company-billing' && connectActive && <CompanyBillingTab />}
        {activeTab === 'revenue' && connectActive && <RevenueTab />}
      </div>
    </div>
  );
}
